import React, { useState, useRef, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Play, Pause, Power, Volume2 } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";

const waveforms = [
  { value: "sine", label: "Sine" },
  { value: "square", label: "Square" },
  { value: "sawtooth", label: "Sawtooth" },
  { value: "triangle", label: "Triangle" },
];

const FrequencyOscillator = ({ 
  index, 
  audioContext, 
  masterGain,
  isPlaying,
  onFrequencyChange
}) => {
  const [enabled, setEnabled] = useState(index === 0);
  const [frequency, setFrequency] = useState(432);
  const [waveform, setWaveform] = useState("sine");
  const [volume, setVolume] = useState(50);
  
  const oscillatorRef = useRef(null);
  const gainNodeRef = useRef(null);

  const createOscillator = useCallback(() => {
    if (!audioContext || !masterGain || !enabled || !isPlaying) return;

    // Clean up existing
    if (oscillatorRef.current) {
      try {
        oscillatorRef.current.stop();
        oscillatorRef.current.disconnect();
      } catch (e) {}
    }

    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.type = waveform;
    oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime);
    gainNode.gain.setValueAtTime(volume / 100, audioContext.currentTime);
    
    oscillator.connect(gainNode);
    gainNode.connect(masterGain);
    oscillator.start();
    
    oscillatorRef.current = oscillator;
    gainNodeRef.current = gainNode;
  }, [audioContext, masterGain, enabled, isPlaying, frequency, waveform, volume]);

  useEffect(() => {
    if (isPlaying && enabled) {
      createOscillator();
    } else if (oscillatorRef.current) {
      try {
        oscillatorRef.current.stop();
        oscillatorRef.current.disconnect();
      } catch (e) {}
      oscillatorRef.current = null;
    }

    return () => {
      if (oscillatorRef.current) {
        try {
          oscillatorRef.current.stop();
          oscillatorRef.current.disconnect();
        } catch (e) {}
      }
    };
  }, [isPlaying, enabled, createOscillator]);

  useEffect(() => {
    if (oscillatorRef.current && audioContext) {
      oscillatorRef.current.frequency.setValueAtTime(frequency, audioContext.currentTime);
    }
    onFrequencyChange?.(index, { frequency, waveform, volume, enabled });
  }, [frequency, audioContext, index, waveform, volume, enabled, onFrequencyChange]);

  useEffect(() => {
    if (oscillatorRef.current) {
      oscillatorRef.current.type = waveform;
    }
  }, [waveform]);

  useEffect(() => {
    if (gainNodeRef.current && audioContext) {
      gainNodeRef.current.gain.setValueAtTime(volume / 100, audioContext.currentTime);
    }
  }, [volume, audioContext]);

  const handleFrequencyInput = (e) => {
    const val = parseFloat(e.target.value);
    if (!isNaN(val) && val >= 0.1 && val <= 20000) {
      setFrequency(val);
    }
  };

  return (
    <div className={cn(
      "glass-card p-4 transition-all duration-300",
      enabled ? "border-violet-500/30" : "opacity-60"
    )}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setEnabled(!enabled)}
            data-testid={`osc-${index}-toggle`}
            className={cn(
              "w-10 h-10 rounded-lg flex items-center justify-center transition-colors",
              enabled ? "bg-violet-600 text-white" : "bg-white/5 text-gray-500"
            )}
          >
            <Power size={18} />
          </motion.button>
          <div>
            <span className="text-sm font-medium text-white">Oscillator {index + 1}</span>
            <span className={cn(
              "ml-2 text-xs px-2 py-0.5 rounded-full",
              enabled ? "bg-green-500/20 text-green-400" : "bg-gray-500/20 text-gray-500"
            )}>
              {enabled ? "ON" : "OFF"}
            </span>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Volume2 size={14} className="text-gray-500" />
          <Slider
            value={[volume]}
            onValueChange={([v]) => setVolume(v)}
            min={0}
            max={100}
            step={1}
            disabled={!enabled}
            data-testid={`osc-${index}-volume`}
            className="w-20"
          />
        </div>
      </div>

      <div className="space-y-4">
        {/* Frequency Input */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs text-gray-500">Frequency</label>
            <div className="flex items-center gap-1">
              <input
                type="number"
                value={frequency}
                onChange={handleFrequencyInput}
                disabled={!enabled}
                data-testid={`osc-${index}-freq-input`}
                className="w-20 px-2 py-1 bg-black/30 border border-white/10 rounded text-right text-sm text-white font-mono focus:outline-none focus:border-violet-500/50"
              />
              <span className="text-xs text-gray-500">Hz</span>
            </div>
          </div>
          <Slider
            value={[Math.log10(frequency) * 25]}
            onValueChange={([v]) => setFrequency(Math.round(Math.pow(10, v / 25) * 10) / 10)}
            min={-25}
            max={107.5}
            step={0.1}
            disabled={!enabled}
            data-testid={`osc-${index}-freq-slider`}
            className="py-2"
          />
          <div className="flex justify-between text-xs text-gray-600 mt-1">
            <span>0.1 Hz</span>
            <span>20 kHz</span>
          </div>
        </div>

        {/* Waveform Selection */}
        <div className="flex gap-1">
          {waveforms.map((wf) => (
            <motion.button
              key={wf.value}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setWaveform(wf.value)}
              disabled={!enabled}
              data-testid={`osc-${index}-wave-${wf.value}`}
              className={cn(
                "flex-1 py-2 text-xs rounded-lg transition-colors",
                waveform === wf.value
                  ? "bg-violet-600 text-white"
                  : "bg-white/5 text-gray-400 hover:bg-white/10"
              )}
            >
              {wf.label}
            </motion.button>
          ))}
        </div>
      </div>
    </div>
  );
};

const FrequencyGenerator = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [masterVolume, setMasterVolume] = useState(70);
  const [oscillatorData, setOscillatorData] = useState([
    { frequency: 432, waveform: "sine", volume: 50, enabled: true },
    { frequency: 528, waveform: "sine", volume: 50, enabled: false },
    { frequency: 639, waveform: "sine", volume: 50, enabled: false },
    { frequency: 852, waveform: "sine", volume: 50, enabled: false },
  ]);
  
  const audioContextRef = useRef(null);
  const masterGainRef = useRef(null);

  const initAudio = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      masterGainRef.current = audioContextRef.current.createGain();
      masterGainRef.current.connect(audioContextRef.current.destination);
      masterGainRef.current.gain.setValueAtTime(masterVolume / 100, audioContextRef.current.currentTime);
    }
    if (audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume();
    }
  }, [masterVolume]);

  useEffect(() => {
    if (masterGainRef.current && audioContextRef.current) {
      masterGainRef.current.gain.setValueAtTime(masterVolume / 100, audioContextRef.current.currentTime);
    }
  }, [masterVolume]);

  const togglePlay = () => {
    if (!isPlaying) {
      initAudio();
    }
    setIsPlaying(!isPlaying);
  };

  const handleOscillatorChange = useCallback((index, data) => {
    setOscillatorData(prev => {
      const newData = [...prev];
      newData[index] = data;
      return newData;
    });
  }, []);

  useEffect(() => {
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  return (
    <div className="space-y-6">
      {/* Header Controls */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white">Frequency Generator</h3>
          <p className="text-sm text-gray-400">Layer up to 4 frequencies</p>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Volume2 size={16} className="text-gray-500" />
            <Slider
              value={[masterVolume]}
              onValueChange={([v]) => setMasterVolume(v)}
              min={0}
              max={100}
              step={1}
              data-testid="master-volume"
              className="w-28"
            />
            <span className="text-xs text-gray-500 w-8">{masterVolume}%</span>
          </div>
          
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={togglePlay}
            data-testid="freq-play-btn"
            className={cn(
              "w-14 h-14 rounded-full flex items-center justify-center transition-all",
              isPlaying 
                ? "bg-gradient-to-r from-red-500 to-orange-500 shadow-[0_0_20px_rgba(239,68,68,0.4)]"
                : "bg-gradient-to-r from-violet-600 to-indigo-600 shadow-[0_0_20px_rgba(124,58,237,0.4)]"
            )}
          >
            {isPlaying ? <Pause size={24} className="text-white" /> : <Play size={24} className="text-white ml-1" />}
          </motion.button>
        </div>
      </div>

      {/* Status */}
      <div className="flex items-center gap-2 text-sm">
        <div className={cn(
          "w-2 h-2 rounded-full",
          isPlaying ? "bg-green-500 animate-pulse" : "bg-gray-600"
        )} />
        <span className="text-gray-400">
          {isPlaying ? "Playing" : "Ready to Play"} • 48kHz Studio Quality
        </span>
      </div>

      {/* Oscillators Grid */}
      <div className="grid md:grid-cols-2 gap-4">
        {[0, 1, 2, 3].map((index) => (
          <FrequencyOscillator
            key={index}
            index={index}
            audioContext={audioContextRef.current}
            masterGain={masterGainRef.current}
            isPlaying={isPlaying}
            onFrequencyChange={handleOscillatorChange}
          />
        ))}
      </div>
    </div>
  );
};

export default FrequencyGenerator;
